import type { ModeLimits } from '@/api/llmConfig';
import type { ComparisonSelectionTotals } from '@/types/chat/chat';

/**
 * "n / 10 files · x / 20 MB" for a comparison (spec §4). The server counts:
 * selected folders expanded, an id selected twice or two documents with the
 * same content counted once, bytes included, and only usable documents. One
 * MB is 1,000,000 bytes. Both limits are compared in exact bytes before any
 * rounding for display; equality is allowed.
 */

export interface ComparisonQuota {
  files: number;
  bytes: number;
  maxFiles: number;
  maxBytes: number;
  minFiles: number;
  overFiles: boolean;
  overBytes: boolean;
  over: boolean;
  tooFew: boolean;
  /** For display only; never compared. */
  mb: string;
  maxMb: string;
}

/**
 * Megabytes for display with one decimal, or as many more as it takes for a
 * total over the limit not to read as the limit itself (20,000,001 bytes is
 * "20.000001", never "20.0").
 */
export const formatMb = (
  bytes: number,
  limitBytes: number | null = null,
  bytesPerMb = 1_000_000,
) => {
  for (let digits = 1; digits <= 6; digits += 1) {
    const text = (bytes / bytesPerMb).toFixed(digits);
    const shown = Math.round(Number(text) * bytesPerMb);
    if (limitBytes == null || bytes <= limitBytes || shown > limitBytes) {
      return text;
    }
  }
  return (bytes / bytesPerMb).toFixed(6);
};

/** A limit in MB without needless decimals (20,000,000 → "20"). */
export const formatLimitMb = (bytes: number, bytesPerMb = 1_000_000) =>
  String(Number((bytes / bytesPerMb).toFixed(6)));

/** Exact bytes, grouped (12,345,678). */
export const formatBytesExact = (bytes: number, locale = 'en-US') =>
  new Intl.NumberFormat(locale).format(bytes);

/**
 * Total bytes of the unique usable documents. Older payloads without
 * `total_bytes` are summed from the kept files.
 */
export const uniqueBytes = (totals: ComparisonSelectionTotals) =>
  typeof totals.total_bytes === 'number'
    ? totals.total_bytes
    : (totals.files ?? [])
        .filter(file => file.usable !== false && file.duplicate_of == null)
        .reduce((sum, file) => sum + Number(file.size_bytes ?? 0), 0);

export const comparisonQuota = (
  totals: ComparisonSelectionTotals,
  limits: ModeLimits['comparison'],
  bytesPerMb = 1_000_000,
): ComparisonQuota => {
  const files = Number(totals.file_count ?? 0);
  const bytes = uniqueBytes(totals);
  const overFiles = files > limits.max_files;
  const overBytes = bytes > limits.max_total_bytes;
  return {
    files,
    bytes,
    maxFiles: limits.max_files,
    maxBytes: limits.max_total_bytes,
    minFiles: limits.min_files,
    overFiles,
    overBytes,
    over: overFiles || overBytes,
    tooFew: files < limits.min_files,
    mb: formatMb(bytes, limits.max_total_bytes, bytesPerMb),
    maxMb: formatLimitMb(limits.max_total_bytes, bytesPerMb),
  };
};

export type BreakdownState = 'counted' | 'duplicate' | 'excluded';

export interface BreakdownRow {
  id: number;
  name: string | null;
  bytes: number | null;
  source: string | null;
  state: BreakdownState;
  /** For excluded rows: pending, deleted, inaccessible, image_only, … */
  reason: string | null;
  /** For duplicates: the document kept in its place. */
  duplicateOf: { id: number; name: string | null } | null;
}

/**
 * Every selected document with what happens to it: counted, merged as a
 * duplicate of another, or excluded with its reason. Counted ones first.
 */
export const quotaBreakdown = (
  totals: ComparisonSelectionTotals,
): BreakdownRow[] => {
  const files = totals.files ?? [];
  const byId = new Map(files.map(file => [Number(file.id), file]));
  const rows: BreakdownRow[] = files.map(file => {
    const duplicate = file.usable !== false && file.duplicate_of != null;
    const excluded = file.usable === false;
    const kept = duplicate ? byId.get(Number(file.duplicate_of)) : undefined;
    return {
      id: Number(file.id),
      name: file.name ?? null,
      bytes: file.size_bytes == null ? null : Number(file.size_bytes),
      source: file.source ?? null,
      state: excluded ? 'excluded' : duplicate ? 'duplicate' : 'counted',
      reason: excluded
        ? (file.exclusion_reason ?? file.unusable_reason ?? 'processing_failed')
        : null,
      duplicateOf: duplicate
        ? { id: Number(file.duplicate_of), name: kept?.name ?? null }
        : null,
    };
  });
  for (const item of totals.excluded ?? []) {
    rows.push({
      id: Number(item.id),
      name: item.name ?? null,
      bytes: null,
      source: null,
      state: 'excluded',
      reason: item.reason,
      duplicateOf: null,
    });
  }
  const order: Record<BreakdownState, number> = {
    counted: 0,
    duplicate: 1,
    excluded: 2,
  };
  return rows.sort((a, b) => order[a.state] - order[b.state]);
};

/** Dictionary keys for exclusion reasons (unknown ones are shown as is). */
export const EXCLUSION_KEYS: Record<string, string> = {
  pending: 'v2.composer.ex_processing',
  processing: 'v2.composer.ex_processing',
  queued: 'v2.composer.ex_processing',
  deleted: 'v2.composer.ex_removed',
  inaccessible: 'v2.composer.ex_inaccessible',
  image_only: 'v2.composer.ex_imageOnly',
  no_text: 'v2.composer.ex_noText',
  processing_failed: 'v2.composer.ex_failed',
};
